import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import API_BASE from "../../../config";
import RoleDashboard from "./RoleDashboard";
import LabRequirementsWidget from "./LabRequirementsWidget";
import m from "./ParentDashboard.module.css";
import { averageOf } from "../../../utils/gradingPeriods";

/** Stable hue per name → drives the avatar tile colour for children
 *  without a photo. Same idiom used across the app so the same child
 *  always renders in the same tone. */
const AVATAR_COLORS = ["#5a4e3a","#3a4e4a","#4a3a5a","#4e4a3a","#3a4a5a","#5a3a3a","#3a5a3a"];
function avatarColor(seed) {
  let h = 0;
  for (let i = 0; i < (seed || "").length; i++) h = (h * 31 + seed.charCodeAt(i)) >>> 0;
  return AVATAR_COLORS[h % AVATAR_COLORS.length];
}
function initials(first, last) {
  const a = (first || "").trim()[0] || "";
  const b = (last  || "").trim()[0] || "";
  return (a + b).toUpperCase() || "?";
}


/** DepEd letter / honor descriptor. Same brackets used by the
 *  /grades page so cards and detail page agree. */
function honorFor(gpa) {
  if (gpa == null) return null;
  if (gpa >= 98) return { label: "Highest Honors", tier: 3 };
  if (gpa >= 95) return { label: "High Honors",    tier: 2 };
  if (gpa >= 90) return { label: "With Honors",    tier: 1 };
  return null;
}
function toneForScore(score) {
  if (score == null) return "none";
  if (score >= 85) return "good";
  if (score >= 75) return "mid";
  return "low";
}

function fmt1(v) {
  if (v == null || !Number.isFinite(v)) return "—";
  return (Math.round(v * 10) / 10).toFixed(1);
}
function fmtMoney(v) {
  const n = Number(v);
  if (!Number.isFinite(n)) return "—";
  return "₱" + n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

/** Fetches the parent's children list. Returns the linked Student rows
 *  with full meta so each child card can render section/grade/term
 *  inline without a second lookup. */
function useParentChildren(parentId) {
  const [state, setState] = useState({ loading: true, children: [], error: "" });
  useEffect(() => {
    if (!parentId) {
      setState({ loading: false, children: [], error: "" });
      return undefined;
    }
    let cancelled = false;
    (async () => {
      try {
        const links = await fetch(`${API_BASE}/api/parent-student?parentId=${parentId}`)
          .then(r => r.ok ? r.json() : []).catch(() => []);
        const children = await Promise.all(
          (Array.isArray(links) ? links : []).map(l =>
            fetch(`${API_BASE}/api/students/${l.studentId}`)
              .then(r => r.ok ? r.json() : null)
              .catch(() => null)
          )
        );
        if (cancelled) return;
        setState({
          loading: false,
          children: children.filter(Boolean),
          error: "",
        });
      } catch (e) {
        if (!cancelled) setState({ loading: false, children: [], error: e.message || "Failed to load" });
      }
    })();
    return () => { cancelled = true; };
  }, [parentId]);
  return state;
}

/** Per-child summary fetcher — grades + tuition invoices in parallel.
 *  Reads endpoints that already gate at the controller layer so we
 *  don't widen any role surface. Resilient to either fetch failing —
 *  the card still renders, just with "—" in the affected stat. */
function useChildSummary(childId) {
  const [state, setState] = useState({
    loading: true,
    gpa: null, subjectsGraded: 0,
    invoicesTotal: 0, invoicesAmount: 0, invoicesUnpaid: 0, invoicesPartial: 0, invoicesPaid: 0,
  });

  useEffect(() => {
    if (!childId) return undefined;
    let cancelled = false;
    setState(p => ({ ...p, loading: true }));
    Promise.all([
      fetch(`${API_BASE}/api/grades?studentId=${childId}`)
        .then(r => r.ok ? r.json() : []).catch(() => []),
      fetch(`${API_BASE}/api/tuition-invoices?studentId=${childId}`)
        .then(r => r.ok ? r.json() : []).catch(() => []),
      fetch(`${API_BASE}/api/semesters`)
        .then(r => r.ok ? r.json() : []).catch(() => []),
    ]).then(([grades, invoices, semesters]) => {
      if (cancelled) return;

      // Group grades by offering → compute subject averages → mean = GPA
      // for the CURRENT semester only. Falls back to overall when no
      // active semester is set so the card never goes blank.
      const active = semesters.find(s => s.active) || null;
      const offeringPairs = {};   // { offeringId: { [periodCode]: number } } — Q1–Q4, MIDTERM/FINAL…
      (Array.isArray(grades) ? grades : []).forEach(g => {
        // The grade row doesn't carry semesterId — it lives on the offering.
        // We approximate "this term" by grouping ALL grades on the active
        // semester's offerings later. Here just stash periods.
        if (!offeringPairs[g.classOfferingId]) offeringPairs[g.classOfferingId] = {};
        offeringPairs[g.classOfferingId][g.period] =
          g.score == null ? null : Number(g.score);
      });
      const averages = Object.values(offeringPairs)
        // Mean of whichever grading periods have scores — same rule as /grades.
        .map(p => averageOf(p))
        .filter(v => v != null);
      const gpa = averages.length === 0
        ? null
        : averages.reduce((a, b) => a + b, 0) / averages.length;

      // Invoice rollup — count by status + total amount across all rows.
      const invs = Array.isArray(invoices) ? invoices : [];
      let total = 0, unpaid = 0, partial = 0, paid = 0;
      for (const inv of invs) {
        const amt = Number(inv.amount);
        if (Number.isFinite(amt)) total += amt;
        const st = String(inv.status || "UNPAID").toUpperCase();
        if (st === "PAID") paid++;
        else if (st === "PARTIAL") partial++;
        else unpaid++;
      }

      setState({
        loading: false,
        gpa,
        subjectsGraded: averages.length,
        invoicesTotal: invs.length,
        invoicesAmount: total,
        invoicesUnpaid: unpaid,
        invoicesPartial: partial,
        invoicesPaid: paid,
      });
      // Active semester is captured for the optional sub-line; not stored
      // in state to keep the hook output stable across renders.
      void active;
    }).catch(() => {
      if (!cancelled) setState(p => ({ ...p, loading: false }));
    });
    return () => { cancelled = true; };
  }, [childId]);

  return state;
}

export default function ParentDashboard({ user }) {
  const parentId = user?.linkedEntity?.id || user?.linkedEntityId || null;
  const { loading, children, error } = useParentChildren(parentId);

  return (
    <RoleDashboard
      eyebrow="Parent"
      title={`Welcome${user?.linkedEntity?.displayName ? `, ${user.linkedEntity.displayName}` : ""}.`}
      sub={children.length > 0
        ? `Live academic and tuition snapshot for ${children.length === 1 ? "your child" : `your ${children.length} children`}.`
        : "Your linked children's snapshot will appear here once an admin links them to your account."}
      user={user}
    >
      <section className={m.kids}>
        <header className={m.kidsHead}>
          <h3 className={m.kidsTitle}>Your children</h3>
          {!loading && children.length > 0 && (
            <span className={m.kidsCount}>{children.length} linked</span>
          )}
        </header>

        {loading && (
          <div className={m.skel}>
            <div className={m.skelCard} />
            <div className={m.skelCard} />
          </div>
        )}

        {!loading && error && (
          <div className={m.error}>{error}</div>
        )}

        {!loading && !error && children.length === 0 && (
          <div className={m.empty}>
            <div className={m.emptyTitle}>No linked children yet</div>
            <div className={m.emptySub}>
              Ask the registrar to link your account to your child's student
              record. Once linked, you'll see their grades, tuition status,
              and class schedule here.
            </div>
          </div>
        )}

        {!loading && !error && children.length > 0 && (
          <div className={m.grid}>
            {children.map((child, idx) => (
              <ChildCard key={child.id} child={child} index={idx} />
            ))}
          </div>
        )}
      </section>
    </RoleDashboard>
  );
}

function ChildCard({ child, index }) {
  const navigate = useNavigate();
  const summary  = useChildSummary(child?.id);
  const honor    = honorFor(summary.gpa);
  const fullName = `${child.lastName || ""}, ${child.firstName || ""}`.replace(/^, |, $/g, "");

  const tuitionTone = summary.invoicesUnpaid > 0
    ? "danger"
    : summary.invoicesPartial > 0
    ? "warn"
    : summary.invoicesTotal > 0 ? "good" : "none";
  const tuitionLabel = summary.invoicesTotal === 0
    ? "No invoices"
    : summary.invoicesUnpaid > 0
    ? `${summary.invoicesUnpaid} unpaid`
    : summary.invoicesPartial > 0
    ? `${summary.invoicesPartial} partial`
    : "Fully paid";

  return (
    <article
      className={m.card}
      style={{ animationDelay: `${Math.min(index, 6) * 60}ms` }}>
      <header className={m.cardHead}>
        {child.photoUrl
          ? <img src={child.photoUrl} alt="" className={m.avatarImg} />
          : <div className={m.avatar}
                 style={{ background: avatarColor(fullName) }}>
              {initials(child.firstName, child.lastName)}
            </div>}
        <div className={m.ident}>
          <div className={m.name} title={fullName}>{fullName || "Unnamed"}</div>
          <div className={m.metaRow}>
            {child.studentNumber && (
              <span className={m.metaMono}>#{child.studentNumber}</span>
            )}
            {child.status && (
              <span className={`${m.statusPill} ${m["statusPill_" + String(child.status).toLowerCase()] || m.statusPill_other}`}>
                {child.status}
              </span>
            )}
          </div>
        </div>
        {honor && (
          <span className={`${m.honorRibbon} ${m["honorTier" + honor.tier]}`}
                title={`Honor tier ${honor.tier}`}>
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none"
                 stroke="currentColor" strokeWidth="2.2"
                 strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <circle cx="12" cy="8" r="6"/>
              <path d="M8.21 13.89 7 22l5-3 5 3-1.21-8.12"/>
            </svg>
            {honor.label}
          </span>
        )}
      </header>

      <div className={m.statRow}>
        <Stat
          label="Term GPA"
          value={summary.loading ? "…" : fmt1(summary.gpa)}
          hint={summary.gpa == null
            ? (summary.loading ? "Loading…" : "No grades yet")
            : honor?.label || `${summary.subjectsGraded} subject${summary.subjectsGraded === 1 ? "" : "s"} graded`}
          tone={toneForScore(summary.gpa)}
        />
        <Stat
          label="Total Invoiced"
          value={summary.loading ? "…" : fmtMoney(summary.invoicesAmount)}
          hint={`${summary.invoicesTotal} invoice${summary.invoicesTotal === 1 ? "" : "s"} on file`}
        />
        <Stat
          label="Tuition Status"
          value={summary.loading ? "…" : tuitionLabel}
          hint={summary.invoicesTotal > 0
            ? `${summary.invoicesPaid} paid · ${summary.invoicesPartial} partial · ${summary.invoicesUnpaid} unpaid`
            : "—"}
          tone={tuitionTone}
        />
      </div>

      <div className={m.actions}>
        <button type="button"
                className={m.actionBtn}
                onClick={() => navigate("/grades")}>
          View grades →
        </button>
        <button type="button"
                className={m.actionBtn}
                onClick={() => navigate("/tuition/soa")}>
          Statement of Account →
        </button>
        <button type="button"
                className={m.actionBtnGhost}
                onClick={() => navigate("/schedule")}>
          Class schedule
        </button>
      </div>

      {/* Items the IT teacher requires for this child. Collapsed by
          default so a parent with many children can scan the card
          stack without one child's requirements pushing the next card
          off-screen. Self-hides when nothing applies. */}
      <LabRequirementsWidget
        studentId={child?.id}
        childName={child?.firstName || fullName || "this child"}
        collapsedByDefault
      />
    </article>
  );
}

function Stat({ label, value, hint, tone }) {
  return (
    <div className={`${m.stat} ${tone ? m["stat_" + tone] : ""}`}>
      <div className={m.statLabel}>{label}</div>
      <div className={m.statValue}>{value}</div>
      <div className={m.statHint} title={hint}>{hint}</div>
    </div>
  );
}
